import { DOCUMENT } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  OnDestroy,
  Inject,
  ChangeDetectorRef
} from '@angular/core';
import { ChangeDetector } from '@wfm/core';
import { SortCriteria } from '../../types/sort-criteria';
import { SortField } from '../../types/sort-field';
import { VirtualGridColumn } from '../../types/virtual-grid-column';

interface SortableItem {
  label: string;
  column: VirtualGridColumn;
  sortField?: SortField;
  checked?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'virtual-grid-card-sort',
  templateUrl: './virtual-grid-card-sort.component.html',
  styleUrls: ['./virtual-grid-card-sort.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridCardSortComponent extends ChangeDetector implements OnChanges, OnDestroy {
  sortableItemList: SortableItem[] = [];
  sortLabel: string;
  sortIndicator: string;
  checkedItem: any = undefined;

  private $isOpen = false;
  get isOpen(): boolean {
    return this.$isOpen;
  }
  set isOpen(v: boolean) {
    this.$isOpen = v;
    this.$detectChanges();
  }

  @Input() layout: VirtualGridColumn[];
  @Input() include: string[];
  @Input() disabledColumns: string[];
  @Input() sortCriteria: SortCriteria[];
  @Input() enableResetSortButton?: boolean = true;

  @Output() resetSort: EventEmitter<any> = new EventEmitter<any>();
  @Output() sortCriteriaChange: EventEmitter<SortCriteria[]> = new EventEmitter<SortCriteria[]>();

  isSorted = false;

  constructor(@Inject(DOCUMENT) document: any, cd: ChangeDetectorRef) {
    super((document as Document).defaultView, cd);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.layout || changes.include || changes.disabledColumns || changes.sortCriteria) {
      this.createSortColumnList();
    }

    if (changes.sortCriteria) {
      this.isSorted = this.sortCriteria?.length > 0;
      this.updateSortLabel();
      this.updateSortIndicator();
      this.updateCheckedState();
    }
  }

  ngOnDestroy() {
    //For WFM usage
  }

  /**
   * Generate sortable item list excluding non-visible columns
   */
  createSortColumnList() {
    this.sortableItemList = [];
    if (this.include && this.layout) {
      const sorted = (this.sortCriteria || [])[0];
      const included = [...this.include];
      sorted && included.indexOf(sorted.column.id) < 0 && included.push(sorted.column.id);

      included.forEach(colId => {
        const column = this.layout.find(col => col.id === colId);
        if (!column) return;
        const disabled = !!(this.disabledColumns || []).find(dis => dis === column?.id);
        if (column.multiFieldItems && column.defaultSort) {
          //Get the multifield items list
          column.multiFieldItems.forEach(field => {
            this.sortableItemList.push({
              label: field.label,
              column: column,
              sortField: field,
              disabled
            });
          });
        } else if (column.defaultSort) {
          this.sortableItemList.push({
            column: column,
            label: column.headerLabel,
            disabled
          });
        }
      });
    }
  }

  /**
   * Update current sort icon
   */
  updateSortIndicator() {
    if (this.sortCriteria?.length) {
      this.sortIndicator = this.sortCriteria[0].isOrderAscending ? 'ico-arrow-up-16' : 'ico-arrow-down-16';
    } else {
      this.sortIndicator = '';
    }
  }

  /**
   * Update item sort state
   */
  updateCheckedState() {
    const sortCriteria = (this.sortCriteria || [])[0];
    this.checkedItem = sortCriteria?.column.id;
  }

  /**
   * Update sorted field label
   */
  updateSortLabel() {
    this.sortLabel = this.getSortLable((this.sortCriteria || [])[0]);
  }

  getSortLable(sortCriteria: SortCriteria) {
    if (sortCriteria?.column?.multiFieldItems) {
      return sortCriteria?.sortField?.label || 'vg.unknown';
    }

    if (sortCriteria) {
      return sortCriteria.column?.headerLabel || 'vg.unknown';
    }

    return 'vg.none';
  }

  closeRequest() {
    this.isOpen = false;
  }

  /**
   * Called upon sort direction change
   */
  onSortToggled() {
    this.sortCriteria = [
      {
        column: this.sortCriteria[0].column,
        isOrderAscending: !this.sortCriteria[0].isOrderAscending,
        sortField: this.sortCriteria[0].sortField
      }
    ];
    this.sortCriteriaChange.emit(this.sortCriteria);
  }

  /**
   * Called upon sort item change
   */
  onSortItemChanged(item: SortableItem) {
    this.sortCriteria = [
      {
        column: item.column,
        isOrderAscending: item.column.defaultSort === 'ascending' ? true : false,
        sortField: item.sortField
      }
    ];
    this.closeRequest();
    this.sortCriteriaChange.emit(this.sortCriteria);
  }

  onResetSort() {
    this.closeRequest();
    this.resetSort.emit();
  }
}
